import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import { withStyles } from "@material-ui/styles";
import Button from "components/button"
import ButtonIcon from "components/button-icon"
import Checkbox from "components/checkbox";
import { IconAlert } from "components/icons/icn-alert.icon"
import IconStop from "components/icons/stop.icon";
import TextArea from "components/textarea"
import { SelectBox } from "components/uikit-adapter";
import { useOrdersContext } from "features/orders/context";
import { useGlobalContext } from "hooks/useGlobalContext";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import Session from "utils/user-storage";
import { isLoadCompositionFlowBrewerySide } from "utils/validations-general-settings";
import TipoPerfilAcesso from "../../../../models/usuarios/tipo-perfil-acesso";
import * as S from "./styled";
import { OrdersCancelDataTable } from "./table";
import { getRequestsToCancel } from "./utils";


const StyledDialogContent = withStyles(() => ({
  root: {
    borderBottom: 'none'
  }
}))(DialogContent)

export const OrdersCancelModal = ({ open, close, data }) => {
  const { startOperation, showFeedback } = useGlobalContext()
  const isBrewerySide = useMemo(() => isLoadCompositionFlowBrewerySide(), [])
  const { TipoUsuario, PerfilAcesso } = Session.get()
  const permissions = [
    TipoPerfilAcesso.PciSolutions,
    TipoPerfilAcesso.MasterData,
    TipoPerfilAcesso.Administrador
  ]
  const { cancellationJustifications } = useOrdersContext();


  const [selectedOrders, setSelectedOrders] = useState(data);
  const [userForcedCancel, setUserForcedCancel] = useState(false);
  const [onlyNotBilledCancel, setOnlyNotBilledCancel] = useState(false);
  const [note, setNote] = useState(null);
  const [generalJustification, setGeneralJustification] = useState("");


  const handleGeneralJustificationChange = (value) => {
    setGeneralJustification(value);
    setSelectedOrders((prev) => {
      return prev.map((item) => ({
        ...item,
        TypeJustificationManualSuggestion: value.Key,
      }))
    }
    );
  };

  const hasPermissionToForcedCancel = () =>
    TipoUsuario === 1 && permissions.includes(PerfilAcesso)

  const isBilled = (status) => status === 8 || status === 9 

  const billedOrder = selectedOrders.find((order) => isBilled(order.OrderStatus))
  const showWarning = selectedOrders.length > 1 || billedOrder;
  const shouldForceCancel = isBrewerySide || userForcedCancel;

  const handleChangeJustificationToARow = (row, justification) => {
    setSelectedOrders(selectedOrders.map(item =>
      item === row
        ? { ...item, TypeJustificationManualSuggestion: justification?.Key ?? null }
        : item
    ));
  }

  const validateOrdersHaveJustifications = (orders) => {
    const hasOrderWithoutJustification = orders.some(order => !order?.TypeJustificationManualSuggestion);
    return !hasOrderWithoutJustification;
  } 

  const handleClose = () => {
    setNote(null);
    setUserForcedCancel(false);
    setOnlyNotBilledCancel(false);
    close();
  }

  const handleSave = () => {
    const items = !onlyNotBilledCancel ? selectedOrders : selectedOrders.filter((item) => !isBilled(item.OrderStatus));
    const ordersHaveJustifications = validateOrdersHaveJustifications(items);

    if (items.length === 0) {
      showFeedback(intl.get("orders.feedback.noOrdersToBeCanceled"));
    } else if (!ordersHaveJustifications) {
      showFeedback(intl.get("orders.feedback.justificationMandatoryToCancelOrder"));
    } else {
      const requests = getRequestsToCancel(items, shouldForceCancel, note);
      const text = intl.get("orders.operationText02");
      startOperation(requests, text);
      handleClose();
    }
  }

  useEffect(() => {
    setSelectedOrders(data);
  }, [data])

  return (
    <Dialog
      PaperProps={{
        style: { overflow: "visible", color: "var(--color-neutral-600)" },
      }}
      fullScreen
      open={open}
    >
      <S.DialogTitle>
        <S.DialogTitleText>{intl.get("orders.cancelOrders")}</S.DialogTitleText>
        <S.DialogTitleIcon onClick={handleClose} />
      </S.DialogTitle>
      <StyledDialogContent dividers>
        <OrdersCancelDataTable
          data={selectedOrders}
          handleChangeJustificationToARow={handleChangeJustificationToARow}
        />
        {showWarning && (
          <S.DialogWarningSection>
            <div className="warning-header-icon-text">
              <IconAlert color={"var(--color-feedback-danger-300)"} />
              <p>{intl.get("orders.operationHeader")}</p>
            </div>
            <div className="warning-header-information-text">
              {selectedOrders.length > 1 && <p>{intl.get("orders.cancelMultipleOrders")}</p>}
              {billedOrder && <p>{intl.get("orders.billedOrdersDescription")}</p>}
            </div>
          </S.DialogWarningSection>
        )}
        <S.DialogOptionsSection>
          {!isBrewerySide && hasPermissionToForcedCancel() && (
            <Checkbox
              text={intl.get("stocks.stockDetail.dangerDialog.forcedCancel")}
              name="order-cancel-forced-checkbox"
              checked={userForcedCancel}
              onChange={() => setUserForcedCancel(!userForcedCancel)}
              data-testid="order-cancel-forced-checkbox"
            />
          )}
          {billedOrder && (
            <Checkbox
              text={intl.get("orders.cancelNotBilledOrders")}
              name="order-cancel-only-not-billed-checkbox"
              checked={onlyNotBilledCancel}
              onChange={() => setOnlyNotBilledCancel(!onlyNotBilledCancel)}
              data-testid="order-cancel-only-not-billed-checkbox"
            />
          )}
        </S.DialogOptionsSection>
        <SelectBox
          name="justificativa-geral"
          label={intl.get("commons.justification")}
          placeholder={intl.get("commons.justification")}
          labelKey="Value"
          valueKey="Key"
          value={generalJustification}
          options={cancellationJustifications}
          onChange={(selected) => handleGeneralJustificationChange(selected)}
          searchable
          clearable
          data-testid="order-cancel-general-justification-selectbox"
          menuPlacement="auto"
        />
        {!billedOrder && (
          <TextArea
            label={intl.get("commons.observation")}
            value={note}
            onChange={(ev) => setNote(ev.target.value)}
            data-testid="order-cancel-modal-observation"
          />
        )}
      </StyledDialogContent>
      <S.DialogActions>
        <ButtonIcon
          buttonType="hidden"
          buttonIcon={<IconStop />}
          buttonText={intl.get("geral.buttonsDefault.cancel")}
          onClick={handleClose}
          data-testid="orders-cancel-modal-cancel-button"
        />
        <Button
          style={{ width: "auto", padding: "12px" }}
          buttonType="primary"
          value={intl.get("geral.buttonsDefault.save")}
          onClick={handleSave}
          data-testid="orders-cancel-modal-save-button"
        />
      </S.DialogActions>
    </Dialog>
  );
}

OrdersCancelModal.propTypes = {
  close: PropTypes.func.isRequired,
  data: PropTypes.shape({
    filter: PropTypes.func,
    find: PropTypes.func
  }).isRequired,
  open: PropTypes.any.isRequired
}
